import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, Nav } from 'react-bootstrap'
import dynamic from 'next/dynamic'
import { useLazyQuery, useSubscription } from '@apollo/client'
import style from '@shared/components/commonNav/style.module.scss'

import styles from '../../../../pages/cricket/live-score/[...matchSlug]/style.module.scss'
import { GET_COMMENTARY } from '@graphql/match/match.query'
import { GET_COMMENTARY_SUBSCRIPTION } from '@graphql/match/match.subscription'
import { isBottomReached } from '@shared-submodule/utils'
import { commentaryLoading } from '@shared/libs/allLoader'
import useStatus from '@shared-submodule/sm-hooks/useStatus'

const Commentary = dynamic(() => import('../commentary'))
const MatchDetailLive = dynamic(() => import('../matchDetailLive'))
const NoData = dynamic(() => import('@shared/components/noData'), { ssr: false })

const MatchDetailCommentary = ({ data, matchDetail, liveScoreData, handleCommentary }) => {
  const [activeTab, setActiveTab] = useState('All')
  const { sStatusStr, sLiveGameStatusStr } = useStatus(matchDetail)
  const [commentaryData, setCommentaryData] = useState(data)
  const [enableBtn, setEnableBtn] = useState(data?.length > 14)
  const [isFilter, setIsFilter] = useState(false)
  const btnDisabled = useRef(false)
  const loading = useRef(false)
  const lastData = useRef(0)
  const payloads = useRef({
    iMatchId: matchDetail?._id,
    nInningNumber: null,
    nLimit: 30
  })

  const [getMoreCommentary, { data: updatedCommentary, loading: isCommentaryLoading }] = useLazyQuery(GET_COMMENTARY)

  typeof window !== 'undefined' && useSubscription(GET_COMMENTARY_SUBSCRIPTION, {
    variables: { input: { iMatchId: matchDetail?._id } },
    onData: ({ data }) => {
      if (data?.data?.listMatchCommentaries?.length) {
        handleCommentary()
        const splitIndex = commentaryData?.findIndex((commentary) => commentary?.sEventId === data?.data?.listMatchCommentaries?.at(-1)?.sEventId)
        const newCommentaryData = commentaryData.slice(splitIndex !== -1 ? splitIndex + 1 : 0, commentaryData.length)
        if (payloads.current && payloads.current?.inningValue) {
          const newUpdatingCommentaryFiltered = data?.data?.listMatchCommentaries.filter((item) => payloads.current.nInningNumber === item.nInningNumber)
          setCommentaryData([...newUpdatingCommentaryFiltered, ...newCommentaryData])
        } else if (payloads.current && payloads.current?.sFilter) {
          const newUpdatingCommentaryFiltered = data?.data?.listMatchCommentaries.filter((item) => {
            return payloads.current?.sFilter.includes(item?.sScore)
          })
          setCommentaryData([...newUpdatingCommentaryFiltered, ...newCommentaryData])
        } else {
          setCommentaryData([...data?.data?.listMatchCommentaries, ...newCommentaryData])
        }
      }
    }
  })

  const handleLoadMore = () => {
    getMoreCommentary({ variables: { input: { ...payloads.current } } })
    setEnableBtn(false)
    btnDisabled.current = true
  }

  useEffect(() => {
    if (commentaryData?.length) {
      const lastItem = commentaryData[commentaryData?.length - 1]
      const id = `${lastItem?.sEventId}${lastItem?.sOver}${lastItem?.nInningNumber}`
      payloads.current.sEventId = lastItem?.sEventId
      // payloads.current.nInningNumber = lastItem?.nInningNumber
      if (btnDisabled.current) {
        loading.current = false
        isBottomReached(id, isReached)
      }
    }
  }, [commentaryData])

  useEffect(() => {
    if (isFilter && updatedCommentary) {
      setEnableBtn(true)
      btnDisabled.current = false
      setCommentaryData([...updatedCommentary?.listMatchCommentaries])
      lastData.current = updatedCommentary?.listMatchCommentaries?.length
      setIsFilter(false)
    } else if (updatedCommentary) {
      setCommentaryData([...commentaryData, ...updatedCommentary?.listMatchCommentaries])
      lastData.current = updatedCommentary?.listMatchCommentaries?.length
    }
  }, [updatedCommentary])

  async function isReached(reach) {
    if (reach && !loading.current && lastData.current > 14) {
      loading.current = true
      getMoreCommentary({ variables: { input: { ...payloads.current } } })
    }
  }

  const is100 = matchDetail?.oFormat?.sValue?.includes('t100')

  function handleTabChange(tab) {
    setActiveTab(tab.navItem)
    setIsFilter(true)
    const { highlightValue, inningValue } = getTabValues(tab)
    updatePayloads(highlightValue, inningValue)
    getMoreCommentary({ variables: { input: { ...payloads.current } } })
  }

  function getTabValues(tab) {
    let highlightValue = null
    let inningValue = 0
    switch (tab.value) {
      case 'all':
        break
      case 'highlights':
        highlightValue = ['4', '6', 'w']
        break
      case 'w':
        highlightValue = ['w']
        break
      case '6':
        highlightValue = ['6']
        break
      case '4':
        if (tab.navItem.includes('Inn') || tab.navItem.includes('Super Over')) {
          if (!isNaN(parseInt(tab.value))) {
            inningValue = parseInt(tab.value)
          }
        } else {
          highlightValue = ['4']
        }
        break
      default:
        if (!isNaN(parseInt(tab.value))) {
          inningValue = parseInt(tab.value)
        }
        break
    }
    return { highlightValue, inningValue }
  }

  function updatePayloads(highlightValue, inningValue) {
    const { sFilter, nInningNumber, sEventId, ...rest } = payloads.current
    payloads.current = {
      ...rest,
      ...(highlightValue && highlightValue.length > 0 ? { sFilter: highlightValue } : {}),
      ...(inningValue !== 0 ? { nInningNumber: inningValue } : {})
    }
  }

  const tabs = useMemo(() => {
    const baseTabs = [
      { navItem: 'All', value: 'all' },
      { navItem: 'Highlights', value: 'highlights' }
    ]
    const availableInnings = matchDetail?.aInning?.map((i) => ({ label: i?.sShortName, value: i?.nInningNumber })) || []

    const inningsTabs = availableInnings.map((inning) => ({
      navItem: inning?.label.replace('Inning', 'Inn'),
      value: `${inning?.value}`
    }))

    const filterTabs = [
      { navItem: 'W', value: 'w' },
      { navItem: '6s', value: '6' },
      { navItem: '4s', value: '4' }
    ]
    return [...baseTabs, ...inningsTabs, ...filterTabs]
  }, [matchDetail?.nLatestInningNumber])
  return (
    <>
      {(sStatusStr === 'live' && sLiveGameStatusStr !== 'innings break') && <MatchDetailLive styles={styles} matchDetail={matchDetail} liveScoreData={liveScoreData} />}
      <Nav
        className='justify-content-start mb-3 mt-3 flex-nowrap text-nowrap overflow-auto '
        variant="pills"
      >
        {tabs?.map((data, i) => {
          return (
            <Nav.Item key={i} onClick={() => { handleTabChange(data) }}
            >
              <div className={`${data?.navItem === activeTab && style.active}  ${data.navItem === activeTab ? `${styles?.text_white} bg-theme-color-orig border-primary` : 'bg-light text-dark border-secondary'} nav-link mx-auto d-flex justify-content-between align-items-center flex-grow-1 cursor-pointer ms-2 me-2`}>{data?.navItem}</div>
            </Nav.Item>
          )
        })}
      </Nav>

      {data?.length !== 0 &&
        <>
          <Commentary is100={is100} data={commentaryData.length ? commentaryData : []} inning={matchDetail?.aInning} />
          {isCommentaryLoading && commentaryLoading()}
          {enableBtn && <div className="text-center my-3">
            <Button className="theme-btn" onClick={() => handleLoadMore()}>{'Load more'}</Button>
          </div>}
        </>
      }
      {sStatusStr !== 'live' && data?.length === 0 && <NoData />}
    </>
  )
}

MatchDetailCommentary.propTypes = {
  data: PropTypes.array,
  matchDetail: PropTypes.object,
  liveScoreData: PropTypes.array,
  handleCommentary: PropTypes.func
}

export default MatchDetailCommentary
